import { TOUCH_SCREEN } from '../layout/features';
import { Tween } from '../animation';
import { Rectangle, Vector } from '../math';
import { Utils } from '../Utils';
import CompositeHitArea from './CompositeHitArea';

var pointerEvents = [
    "mousedown",
    "mouseup",
    "mousemove",
    "click",
    "touchstart",
    "touchend",
    "touchmove",
    "tap",
    "mouseover",
    "mouseout",
    "mouseupoutside",
    "rightclick",
    "rightdown",
    "rightup",
    "rightupoutside",
    "touchendoutside",
    "pointerdown",
    "pointerup",
    "pointermove",
    "pointerclick",
    "pointerupoutside"
];

var mouseEvents = [
    "mousedown",
    "mouseup",
    "mousemove",
    "click",
    "mouseover",
    "mouseout",
    "mouseupoutside",
    "rightclick",
    "rightdown",
    "rightup",
    "rightupoutside"
];

var touchEvents = [
    "touchstart",
    "touchend",
    "touchmove",
    "tap",
    "touchendoutside"
];

var TextureCache = {};

/**
 * @class
 * @inheritDoc
 * @classdesc Спрайт
 * @memberOf module:display
 * @augments PIXI.extras.AnimatedSprite
 */

const __BaseClass = PIXI.extras.AnimatedSprite || PIXI.extras.MovieClip;
class Sprite extends __BaseClass {

    /**
     * @param {Asset} [asset=null] ассет или массив ассетов, или null в случае спрайта-пустышки
     */
    constructor(asset=null) {

        let frames = asset ? Sprite.getFrames(asset) : [PIXI.Texture.EMPTY];
        super(frames);

        this.asset = asset;

        this.anchor.set(0.5);

        this.animationSpeed = 24/60;

        this._zIndex = 0;
        this._dragPoint = null;

        if(this.totalFrames > 1) this.gotoAndPlay(0);
        else this.gotoAndStop(0);
    }

    gotoAndStop(...args) {
        var ret = super.gotoAndStop(...args);
        if(this.updateTexture) this.updateTexture();
        return ret;
    }

    gotoAndPlay(...args) {
        var ret = super.gotoAndPlay(...args);
        if(this.updateTexture) this.updateTexture();
        return ret;
    }

    static create(asset) {
        return new Sprite(asset);
    }

    on(event, fn, context) {

        /**
         * Враппер событий mousedown и touchstart. Использовать для однотипного поведения на десктопе и мобильных.
         * @event module:display.Sprite#pointerdown
         */
        if(event == "pointerdown") {
            super.on("mousedown", fn, context);
            super.on("touchstart", fn, context);
        }

        /**
         * Враппер событий mouseup и touchend. Использовать для однотипного поведения на десктопе и мобильных.
         * @event module:display.Sprite#pointerup
         */
        if(event == "pointerup") {
            super.on("mouseup", fn, context);
            super.on("touchend", fn, context);
        }

        /**
         * Враппер событий mousemove и touchmove. Использовать для однотипного поведения на десктопе и мобильных.
         * @event module:display.Sprite#pointermove
         */
        if(event == "pointermove") {
            super.on("mousemove", fn, context);
            super.on("touchmove", fn, context);
        }

        /**
         * Враппер событий click и tap. Использовать для однотипного поведения на десктопе и мобильных.
         * @event module:display.Sprite#pointerclick
         */
        if(event == "pointerclick") {
            super.on("click", fn, context);
            super.on("tap", fn, context);
        }

        /**
         * Враппер событий mouseupoutside и touchendoutside. Использовать для однотипного поведения на десктопе и мобильных.
         * @event module:display.Sprite#pointerupoutside
         */
        if(event == "pointerupoutside") {
            super.on("mouseupoutside", fn, context);
            super.on("touchendoutside", fn, context);
        }

        if(pointerEvents.indexOf(event) >= 0) this.interactive = true;

        super.on(event, fn, context);
    }

    once(event, fn, context) {

        if(event == "pointerdown") {
            super.once("mousedown", fn, context);
            super.once("touchstart", fn, context);
        }

        if(event == "pointerup") {
            super.once("mouseup", fn, context);
            super.once("touchend", fn, context);
        }

        if(event == "pointermove") {
            super.once("mousemove", fn, context);
            super.once("touchmove", fn, context);
        }

        if(event == "pointerclick") {
            super.once("click", fn, context);
            super.once("tap", fn, context);
        }

        if(event == "pointerupoutside") {
            super.once("mouseupoutside", fn, context);
            super.once("touchendoutside", fn, context);
        }

        if(pointerEvents.indexOf(event) >= 0) this.interactive = true;

        super.once(event, fn, context);
    }

    removeListener(event, fn, context, once) {
        if(event == "pointerdown") {
            super.removeListener("mousedown", fn, context, once);
            super.removeListener("touchstart", fn, context, once);
        }

        if(event == "pointerup") {
            super.removeListener("mouseup", fn, context, once);
            super.removeListener("touchend", fn, context, once);
        }

        if(event == "pointermove") {
            super.removeListener("mousemove", fn, context, once);
            super.removeListener("touchmove", fn, context, once);
        }

        if(event == "pointerclick") {
            super.removeListener("click", fn, context, once);
            super.removeListener("tap", fn, context, once);
        }

        if(event == "pointerupoutside") {
            super.removeListener("mouseupoutside", fn, context);
            super.removeListener("touchendoutside", fn, context);
        }

        super.removeListener(event, fn, context, once);

        if(pointerEvents.indexOf(event) >= 0) {
            let ok = false;
            for(let t of pointerEvents) {
                if(this.listeners(t).length > 0) {
                    ok = true;
                    break;
                }
            }
            this.interactive = ok;
        }
    }

    off(event, fn, context, once) {
        this.removeListener(event, fn, context, once);
    }

    emit(event) {

        var a1 = arguments[1];

        if(!a1) a1 = {};

        if (pointerEvents.indexOf(event) >= 0) {

            if(TOUCH_SCREEN && touchEvents.indexOf(event) < 0) return;
            if(!TOUCH_SCREEN && mouseEvents.indexOf(event) < 0) return;

            if(!a1.data) a1.data = {};
            a1.data.local = a1.data.getLocalPosition(this);
        }

        if(!a1.type) a1.type = event;
        a1.target = this;

        if(arguments.length < 3) super.emit(event, a1);
        if(arguments.length == 3) super.emit(event, a1, arguments[2]);
        if(arguments.length == 4) super.emit(event, a1, arguments[2], arguments[3]);
        if(arguments.length >= 5) super.emit(event, a1, arguments[2], arguments[3], arguments[4]);
    }

    /**
     * Очищает всю анимацию для объекта
     */
    removeTweens() {
        Tween.destroy(Tween.findByTarget(this));
        Tween.destroy(Tween.findByTarget(this.scale));
        Tween.destroy(Tween.findByTarget(this.skew));
    }

    /**
     * Создаёт анимацию
     * @return {Tween}
     */
    addTween(prop, end, duration, ease, onfinish, onchange, obj) {
        if(!obj) obj = this;
        var t = new Tween(obj, prop, obj[prop], end, duration, ease);
        if (onchange) t.on('change', onchange);
        if (onfinish) t.on('finish', onfinish);
        return t;
    }

    /**
     * Перемещение объекта в указанную точку. Можно использовать в цепочке.
     * @param {Number} x координата X
     * @param {Number} y координата Y
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {DisplayObject}
     */
    moveTo(x, y, duration, ease, onfinish, onchange) {

        this.addTween('x', x, duration, ease, null, null).play();
        this.addTween('y', y, duration, ease, onfinish, onchange).play();
        return this;
    }

    /**
     * Перемещение объекта на указанное расстояние. Можно использовать в цепочке.
     * @param {Number} x координата X
     * @param {Number} y координата Y
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {Sprite}
     */
    moveBy(x, y, duration, ease, onfinish, onchange) {
        return this.moveTo(this.x + x, this.y + y, duration, ease, onfinish, onchange);
    }

    /**
     * Изменение прозрачности объекта до указанного значения
     * @param {Number} alpha Прозрачность (0-1)
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {DisplayObject}
     */
    fadeTo(alpha, duration, ease, onfinish, onchange) {
        this.addTween('alpha', alpha, duration, ease, onfinish, onchange).play();
        return this;
    }

    /**
     * Изменение прозрачности объекта на указанное значение
     * @param {Number} alpha Прозрачность (0-1)
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {Sprite}
     */
    fadeBy(alpha, duration, ease, onfinish, onchange) {
        var val = Math.max(0, Math.min(1, this.alpha + alpha));
        return this.fadeTo(val, duration, ease, onfinish, onchange);
    }

    /**
     * Поворот объекта до указанного угла
     * @param {Number} rotation Угол в радианах
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {DisplayObject}
     */
    rotateTo(rotation, duration, ease, onfinish, onchange) {
        this.addTween('rotation', rotation, duration, ease, onfinish, onchange).play();
        return this;
    }

    /**
     * Поворот объекта на указанный угол
     * @param {Number} rotation Угол в радианах
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {Sprite}
     */
    rotateBy(rotation, duration, ease, onfinish, onchange) {
        return this.rotateTo(this.rotation + rotation, duration, ease, onfinish, onchange);
    }

    /**
     * Искривление объекта по оси x до указанного угла
     * @param {Number} skew Угол в радианах
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {DisplayObject}
     */
    skewXTo(skew, duration, ease, onfinish, onchange) {
        this.addTween('x', skew, duration, ease, onfinish, onchange, this.skew).play();
        return this;
    }

    /**
     * Искривление объекта по оси x на указанный угол
     * @param {Number} skew Угол в радианах
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {Sprite}
     */
    skewXBy(skew, duration, ease, onfinish, onchange) {
        return this.skewXTo(this.skew.x + skew, duration, ease, onfinish, onchange);
    }

    /**
     * Искривление объекта по оси y до указанного угла
     * @param {Number} skew Угол в радианах
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {DisplayObject}
     */
    skewYTo(skew, duration, ease, onfinish, onchange) {
        this.addTween('y', skew, duration, ease, onfinish, onchange, this.skew).play();
        return this;
    }

    /**
     * Искривление объекта по оси y на указанный угол
     * @param {Number} skew Угол в радианах
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {Sprite}
     */
    skewYBy(skew, duration, ease, onfinish, onchange) {
        return this.skewYTo(this.skew.y + skew, duration, ease, onfinish, onchange);
    }

    /**
     * Изменение масштаба по x
     * @param {Number} scale Масштаб
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {Sprite}
     */
    scaleXTo(scale, duration, ease, onfinish, onchange) {
        this.addTween('x', scale, duration, ease, onfinish, onchange, this.scale).play();
        return this;
    }

    /**
     * Изменение масштаба на значение по x
     * @param {Number} scale На сколько изменить масштаб
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {Sprite}
     */
    scaleXBy(scale, duration, ease, onfinish, onchange) {
        this.addTween('x', this.scale.x + scale, duration, ease, onfinish, onchange, this.scale).play();
        return this;
    }

    /**
     * Изменение масштаба по y
     * @param {Number} scale Масштаб
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {Sprite}
     */
    scaleYTo(scale, duration, ease, onfinish, onchange) {
        this.addTween('y', scale, duration, ease, onfinish, onchange, this.scale).play();
        return this;
    }

    /**
     * Изменение масштаба на значение по y
     * @param {Number} scale На сколько изменить масштаб
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {Sprite}
     */
    scaleYBy(scale, duration, ease, onfinish, onchange) {
        this.addTween('y', this.scale.y + scale, duration, ease, onfinish, onchange, this.scale).play();
        return this;
    }

    /**
     * Пропорциональное изменение масштаба
     * @param {Number} scale Масштаб
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {DisplayObject}
     */
    scaleTo(scale, duration, ease, onfinish, onchange) {
        this.scaleXTo(scale, duration, ease, null, null);
        this.scaleYTo(scale, duration, ease, onfinish, onchange);
        return this;
    }

    /**
     * Пропорциональное изменение масштаба на значение
     * @param {Number} scale На сколько изменить масштаб
     * @param {Number} duration Необязательная длительность в фреймах
     * @param {Function} [ease] функция анимации (см. <a href="Easing.html">Easing</a>)
     * @param {Function} [onfinish] callback завершения (см. <a href="Tween.html#onfinish">Tween</a>)
     * @param {Function} [onchange] callback шага анимации (см. <a href="Tween.html#onchange">Tween</a>)
     * @return {DisplayObject}
     */
    scaleBy(scale, duration, ease, onfinish, onchange) {
        this.scaleXBy(scale, duration, ease, null, null);
        this.scaleYBy(scale, duration, ease, onfinish, onchange);
        return this;
    }

    /**
     * Проверка вхождения точки в спрайт
     * @param x
     * @param y
     * @returns {boolean}
     */
    hitTestPoint(x, y) {
        let point;

        if(y === undefined) point = x;
        else point = new PIXI.Point(x, y);

        return this.containsPoint(point);
    }

    /**
     * Вычисление абсолютного вращения
     * @returns {number}
     */
    getAbsoluteRotation() {
        let r = this.rotation;
        let parent = this.parent;
        while(parent) {
            r += parent.rotation;
            parent = parent.parent;
        }
        return r;
    }

    /**
     * Вычисление абсолютного масштаба по x
     * @returns {number}
     */
    getAbsoluteScaleX() {
        let scale = this.scale.x;
        let parent = this.parent;
        while(parent) {
            scale *= parent.scale.x;
            parent = parent.parent;
        }
        return scale;
    }

    /**
     * Вычисление абсолютного масштаба по y
     * @returns {number}
     */
    getAbsoluteScaleY() {
        let scale = this.scale.y;
        let parent = this.parent;
        while(parent) {
            scale *= parent.scale.y;
            parent = parent.parent;
        }
        return scale;
    }

    /**
     * Вычисление абсолютных координат центра спрайта
     * @returns {number}
     */
    getGlobalCenter() {
        if(this.anchor.x == 0.5 && this.anchor.y == 0.5) return this.getGlobalPosition();

        let dx = 0.5 - this.anchor.x;
        let dy = 0.5 - this.anchor.y;
        let angle = Math.atan2(dy, dx) + this.getAbsoluteRotation();
        let w = this.width * Math.abs(this.getAbsoluteScaleX()) * Math.abs(dx);
        let h = this.height * Math.abs(this.getAbsoluteScaleY()) * Math.abs(dy);
        let len = Math.sqrt(w*w + h*h);

        let p = this.getGlobalPosition();

        p.x += Math.cos(angle) * len;
        p.y += Math.sin(angle) * len;

        return p;
    }

    /**
     * Проверка на пересечение с другим спрайтом
     * @param {Sprite} obj
     * @returns {boolean}
     */
    hitTest(obj) {
        let r1 = this.getAbsoluteRotation();
        let r2 = obj.getAbsoluteRotation();

        if(r1 == 0 && r2 == 0) {
            let c1 = this.getGlobalCenter();
            let c2 = obj.getGlobalCenter();
            let cW1 = this.width * Math.abs(this.getAbsoluteScaleX());
            let cH1 = this.height * Math.abs(this.getAbsoluteScaleY());
            let cW2 = obj.width * Math.abs(obj.getAbsoluteScaleX());
            let cH2 = obj.height * Math.abs(obj.getAbsoluteScaleY());
            let cX1 = c1.x - cW1 / 2;
            let cY1 = c1.y - cH1 / 2;
            let cX2 = c2.x - cW2 / 2;
            let cY2 = c2.y - cH2 / 2;

            let top = Math.max(cY1, cY2);
            let left = Math.max(cX1, cX2);
            let right = Math.min(cX1 + cW1, cX2 + cW2);
            let bottom = Math.min(cY1 + cH1, cY2 + cH2);
            let width = right - left;
            let height = bottom - top;

            return (width > 0 && height > 0);
        }
        else {
            let r1 = this.getDrawRectangle(), r2 = obj.getDrawRectangle();
            return r1.hitTestRectangle(r2);
        }
    }

    /**
     * Определение AABB спрайта
     * @returns {Rectangle}
     */
    getDrawRectangle() {
        let c = this.getGlobalCenter();
        let r = new Rectangle(0, 0, this.width * Math.abs(this.getAbsoluteScaleX()), this.height * Math.abs(this.getAbsoluteScaleY()), this.getAbsoluteRotation());
        r.move(c.x, c.y);
        return r;
    }

    /**
     * Проверка на наличие дочернего объекта
     * @param obj
     * @returns {boolean}
     */
    contains(obj) {
        return this.children.indexOf(obj) >= 0;
    }

    /**
     * Клонирование спрайта
     * @returns {Sprite}
     */
    clone() {
        var spr = new Sprite(this.asset);
        Object.assign(spr, this);
        spr.parent = null;
        return spr;
    }

    get zIndex() {
        return this._zIndex;
    }
    set zIndex(val) {
        this._zIndex = val;

        if(this.parent) this.applyZIndex();
        else this.once("added", this.applyZIndex, this);
    }

    /** @ignore */
    applyZIndex() {
        var finished = false;
        while(!finished) {
            finished = true;
            for (var i=0; i<this.parent.children.length-1; i++) {
                var child1 = this.parent.children[i];
                var child2 = this.parent.children[i+1];
                if(child1.zIndex !== undefined) {
                    if(child2.zIndex === undefined || child1.zIndex > child2.zIndex) {
                        this.parent.children[i] = child2;
                        this.parent.children[i+1] = child1;
                        finished = false;
                    }
                }
            }
        }
    }

    /** @ignore */
    doDrag(e) {
        if(!this._dragPoint) return;

        if(e.data.originalEvent.touches && e.data.originalEvent.touches.length != 1) return;

        var x = e.data.global.x - this._dragPoint.x * this.scale.x;
        var y = e.data.global.y - this._dragPoint.y * this.scale.y;
        var p = this.parent.globalToLocal(x, y);
        this.position.set(p.x, p.y);

        /**
         * Событие, возникающее в процессе drag`n`drop
         * @event module:display.Sprite#dragged
         */
        this.emit("dragged");
    }

    /**
     * Начало drag`n`drop
     * @param x
     * @param y
     */
    startDrag(x, y) {
        this._dragPoint = new PIXI.Point(x, y);
        this.on("pointermove", this.doDrag);
    }

    /**
     * Окончание drag`n`drop
     */
    stopDrag() {
        this._dragPoint = null;
        this.off("pointermove", this.doDrag);
    }

    /**
     * Перевод локальной координаты в глобальную
     * @param x
     * @param y
     * @returns {Vector}
     */
    localToGlobal(x, y) {
        var p = (( typeof x == 'object') && ( typeof x['x'] != 'undefined') && ( typeof x['y'] != 'undefined')) ? new Vector(x.x + 0, x.y + 0) : new Vector(x, y);
        //p.rotate(this.getAbsoluteRotation()).add(this.getGlobalPosition());
		p.rotate(-this.getAbsoluteRotation()).add(this.getGlobalPosition());;
        return p;
    }

    /**
     * Перевод глобальной координаты в локальную
     * @param x
     * @param y
     * @returns {Vector}
     */
    globalToLocal(x, y) {
        var p = (( typeof x == 'object') && ( typeof x['x'] != 'undefined') && ( typeof x['y'] != 'undefined')) ? new Vector(x.x + 0, x.y + 0) : new Vector(x, y);
        p.subtract(this.getGlobalPosition()).rotate(this.getAbsoluteRotation());
        return p;
    }

    /**
     * Перевод локальной координаты в систему координат другого спрайта
     * @param x
     * @param y
     * @param {Sprite} target
     * @returns {Vector}
     */
    localToLocal(x, y, target) {
        return target.globalToLocal(this.localToGlobal(x, y));
    }

    update(delta) {
        var currentFrame = this.currentFrame;

        super.update(delta);

        if(this.playing && currentFrame != this.currentFrame) {
            var cnt = 0;
            if(this.currentFrame > currentFrame) cnt = this.currentFrame - currentFrame;
            else cnt = this.currentFrame + this.totalFrames - currentFrame;

            var frame = currentFrame;
            for(var i=0; i<cnt; i++) {
                /**
                 * Смена текущего кадра
                 * @event module:display.Sprite#changeframe
                 */
                this.emit("changeframe", {frame: frame});
                frame++;
                if(frame >= this.totalFrames) {
                    frame = 0;
                    /**
                     * Окончание анимации
                     * @event module:display.Sprite#animationend
                     */
                    this.emit("animationend");
                }
            }
        }
    }

    /** Очистка всех твинов и лиснеров спрайта */
    clear() {
        this.removeTweens();
        this.removeAllListeners();
        this.stop();
    }

    /** Безопасное удаление дочернего элемента с очисткой всех лиснеров и твинов */
    safeRemoveChild(...children) {
        super.removeChild(...children);

        for(var child of children) {
            if(child.safeRemove) child.safeRemove();
        }
    }

    /** Безопасное удаление дочернего элемента по индексу с очисткой всех лиснеров и твинов */
    safeRemoveChildAt(index) {
        var child = super.removeChildAt(index);
        if(child && child.safeRemove) child.safeRemove();
    }

    /** Безопасное удаление дочерних элементов по индексу (от-до) с очисткой всех лиснеров и твинов */
    safeRemoveChildren(beginIndex, endIndex) {
        var removed = super.removeChildren(beginIndex, endIndex);
        for(var child of removed) {
            if(child.safeRemove) child.safeRemove();
        }
    }

    /** Безопасное удаление спрайта с очисткой всех лиснеров и твинов */
    safeRemove() {
        if(this.children) {
            /*
            while (this.children.length) {
                var child = this.children[0];
                if (child.safeRemove) child.safeRemove();
                else this.removeChild(child);
            }
            */
            this.safeRemoveChildren();
        }

        if(this.parent) this.parent.removeChild(this);

        this.clear();
    }

    /** @ignore */
    _doDrawHitArea(area, view) {
        if(area instanceof PIXI.Rectangle) {
            view.drawRect(area.x, area.y, area.width, area.height);
        }
        else if(area instanceof PIXI.RoundedRectangle) {
            view.drawRoundedRect(area.x, area.y, area.width, area.height, area.radius);
        }
        else if(area instanceof PIXI.Circle) {
            view.drawCircle(area.x, area.y, area.radius);
        }
        else if(area instanceof PIXI.Ellipse) {
            view.drawEllipse(area.x, area.y, area.width, area.height)
        }
        else if(area instanceof PIXI.Polygon) {
            view.moveTo(area.points[0], area.points[1]);
            for(let i=2; i<area.points.length; i+=2) {
                view.lineTo(area.points[i], area.points[i+1]);
            }
        }
        else if(area instanceof CompositeHitArea) {
            for(let cArea of area.areas) this._doDrawHitArea(cArea, view);
        }
    }

    /** Хелпер, который добавляет визуальное представление текущей hitArea спрайта. Полезно во время подбора hitArea. */
    drawHitArea() {
        if(!this.hitArea) return;
        if(this.hitAreaView) this.removeChild(this.hitAreaView);

        let view = new PIXI.Graphics();
        view.beginFill(0xff0000, 1);
        view.lineStyle(1, 0x000000);

        this._doDrawHitArea(this.hitArea, view);

        view.endFill();

        this.addChild(view);
        this.hitAreaView = view;
    }

    /** Изменение размера фреймов спрайта */
    resize(width, height) {
        if(this.width == width && this.height == height) return;

        for(var texture of this.textures) {
            texture.frame.width = width;
            texture.frame.height = height;
            texture._updateUvs();
        }
    }

    /** Клонирование всех текстур спрайта для отвязки от кеша */
    cloneTextures() {
        var textures = [];
        for(var tex of this.textures) {
            let frame = new PIXI.Rectangle(tex.frame.x, tex.frame.y, tex.frame.width, tex.frame.height);
            let orig = new PIXI.Rectangle(tex.orig.x, tex.orig.y, tex.orig.width, tex.orig.height);
            let trim = undefined;
            if(tex.trim) trim = new PIXI.Rectangle(tex.trim.x, tex.trim.y, tex.trim.width, tex.trim.height);
            let rotate = tex.rotate;

            textures.push(new PIXI.Texture(tex.baseTexture, frame, orig, trim, rotate));
        }
        this.textures = textures;
        this.texture = this.textures[this.currentFrame];
    }

	/** Установка прерывания всех интерактивных событий */
	preventInteractiveEvents() {
        this.interactive = true;
        this.on("pointerdown", Utils.preventInteractiveEvent);
        this.on("pointerup", Utils.preventInteractiveEvent);
        this.on("pointermove", Utils.preventInteractiveEvent);
        this.on("pointerclick", Utils.preventInteractiveEvent);
        this.on("pointerupoutside", Utils.preventInteractiveEvent);
        this.on("mouseover", Utils.preventInteractiveEvent);
        this.on("mouseout", Utils.preventInteractiveEvent);
    }

    /** Получение списка текстур кадров для PIXI */
    static getFrames(assets) {

        if(!Array.isArray(assets)) assets = [assets];

        let frames = [];

        for(let asset of assets) {
            let texture = PIXI.utils.TextureCache[asset.bitmap.src].baseTexture;

            if (!texture) {
                throw Error(`Texture ${asset.bitmap.src} not found`);
            }

            let totalFrames = asset.totalFrames || asset.frames * asset.layers;

            let frame = 0, layer = 0;
            for (let i = 0; i < totalFrames; i++) {
                let name = texture.imageUrl + "_" + i;
                if (TextureCache[name]) {
                    frames.push(TextureCache[name]);
                }
                else {
                    let tex = new PIXI.Texture(texture);
                    tex.frame = new PIXI.Rectangle(layer * asset.width, frame * asset.height, asset.width, asset.height);
                    frames.push(tex);
                    TextureCache[name] = tex;
                }

                frame++;
                if (frame >= asset.frames) {
                    frame = 0;
                    layer++;
                }
            }
        }

        return frames;
    }
}

export default Sprite;